import { useState } from "react";
import FormInput from "../../../../components/Form/FormInput";
import FormSelect from "../../../../components/Form/FormSelect";
import SystemValueForm from "../../../../components/SystemValue/SystemValueForm";
import { TAX_TYPE_OPTIONS } from "./taxFormat";

const validateField = (name, form) => {
  const value = form[name];
  if (name === "name" && !value.trim()) return "Name is required.";
  if (name === "type" && !value) return "Type is required.";
  if (name === "value") {
    if (value === "") return "Default Value is required.";
    const n = Number(value);
    if (Number.isNaN(n)) return "Default Value must be a number.";
    if (n < 0) return "Default Value cannot be negative.";
    if (form.type === "percentage" && n > 100) return "A percentage cannot be more than 100.";
  }
  return "";
};

const FIELDS = ["name", "type", "value"];
 
const PartyTaxForm = ({ taxToEdit, currency, onSave, onCancel, noun = "Tax" }) => {
  const [form, setForm] = useState(() => ({
    name: taxToEdit?.name ?? "",
    type: taxToEdit?.type ?? "",
    value: taxToEdit ? String(Number(taxToEdit.value)) : "",
  }));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const absolute = form.type === "absolute";

  const handleChange = (e) => {
    const { name, value } = e.target;
    const next = { ...form, [name]: value };
    setForm(next);
    setErrors((prev) => ({
      ...prev,
      [name]: "",
      ...(name === "type" ? { value: prev.value && validateField("value", next) } : {}),
    }));
    setFormError("");
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, { ...form, [name]: value }) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const next = Object.fromEntries(FIELDS.map((f) => [f, validateField(f, form)]));
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSubmitting(true);
    const failure = await onSave({
      name: form.name.trim(),
      type: form.type,
      value: Number(form.value),
    });
    setSubmitting(false);

    if (failure?.field) setErrors((prev) => ({ ...prev, [failure.field]: failure.message }));
    else if (failure) setFormError(failure.message);
  };

  return (
    <SystemValueForm
      title={taxToEdit ? `Edit ${noun}` : `Add ${noun}`}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      error={formError}
      submitting={submitting}
      submitLabel={taxToEdit ? "Update" : "Add"}
    >
      <FormInput
        label="Name"
        name="name"
        value={form.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.name}
        placeholder="e.g. GST"
        required
      />
      <FormSelect
        label="Type"
        name="type"
        value={form.type}
        onChange={handleChange}
        onBlur={handleBlur}
        options={TAX_TYPE_OPTIONS}
        error={errors.type}
        required
      />
      <FormInput
        label={form.type === "percentage" ? "Default Value (%)" : "Default Value"}
        name="value"
        type="number"
        value={form.value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={errors.value}
        placeholder={form.type === "percentage" ? "e.g. 18" : "e.g. 50.00"}
        suffix={absolute ? currency?.code || "No currency" : undefined}
        required
      />
    </SystemValueForm>
  );
};

export default PartyTaxForm;